import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertTriangle, Navigation, User, Stethoscope, Flame, Lock, HelpCircle } from 'lucide-react';
import C from '../../constants/colors';
import { EMERGENCY_META } from '../../constants/meta';
import StatusDot from '../ui/StatusDot';

export default function LivePOBModal({ employee, activeEvents, sites, zones, companies, onScan, onClose }) {
  const [gps, setGps] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(true);
  const [gpsError, setGpsError] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [action, setAction] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => { setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGpsLoading(false); },
        () => { setGpsLoading(false); setGpsError(true); setShowManual(true); },
        { timeout: 8000 }
      );
    } else {
      setGpsLoading(false); setGpsError(true); setShowManual(true);
    }
  }, []);

  const effectiveGps = gps || (manualLat && manualLng ? { lat: parseFloat(manualLat), lng: parseFloat(manualLng) } : null);

  function handleAction(act) {
    setAction(act);
    if (act !== 'NEED_HELP') {
      onScan({ employeeId: employee.id, action: act, emergencyType: null, gps: effectiveGps });
      setDone(true);
    }
  }

  function handleEmergencyType(type) {
    onScan({ employeeId: employee.id, action: 'NEED_HELP', emergencyType: type, gps: effectiveGps });
    setDone(true);
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 };
  const card = { background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '20px', width: '420px', maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };

  if (done) {
    return (
      <div style={overlay}>
        <div style={{ ...card, alignItems: 'center', justifyContent: 'center', padding: '40px', textAlign: 'center' }}>
          <CheckCircle size={56} color={action === 'NEED_HELP' ? C.red : C.green} style={{ marginBottom: '16px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: 700, color: C.textPrimary, marginBottom: '8px' }}>
            {action === 'NEED_HELP' ? 'Alert Sent' : 'Status Updated'}
          </h3>
          <p style={{ fontSize: '14px', color: C.textSecondary, marginBottom: '6px' }}>{employee.name}</p>
          {effectiveGps && (
            <p style={{ fontSize: '12px', color: C.green, fontFamily: 'JetBrains Mono, monospace', marginBottom: '20px' }}>
              GPS: {effectiveGps.lat.toFixed(5)}, {effectiveGps.lng.toFixed(5)}
            </p>
          )}
          <button onClick={onClose} style={{ padding: '10px 28px', borderRadius: '10px', border: 'none', background: C.blue, color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '11px', color: C.blue, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>LivePOB</div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>Report Status</h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}><X size={20} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {/* Employee chip */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <User size={16} color={C.blue} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>{employee.name}</div>
              <div style={{ fontSize: '11px', color: C.textMuted }}>{employee.employeeId} · {employee.department}</div>
            </div>
            <StatusDot status={employee.status} size={8} style={{ marginLeft: 'auto' }} />
          </div>

          {/* GPS section */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Navigation size={14} color={C.blue} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Location (GPS)</span>
            </div>
            {gpsLoading && (
              <div style={{ fontSize: '13px', color: C.blue, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div className="spin" style={{ width: '12px', height: '12px', border: `2px solid ${C.blue}33`, borderTopColor: C.blue, borderRadius: '50%', flexShrink: 0 }} />
                Acquiring GPS from device...
              </div>
            )}
            {!gpsLoading && gps && !showManual && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: C.green, fontFamily: 'JetBrains Mono, monospace', flex: 1 }}>
                  {gps.lat.toFixed(6)}, {gps.lng.toFixed(6)}
                </span>
                <button onClick={() => { setGps(null); setShowManual(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: '11px', padding: '2px 6px' }}>Edit</button>
              </div>
            )}
            {!gpsLoading && gpsError && !gps && (
              <div style={{ fontSize: '12px', color: C.amber, marginBottom: '8px' }}>GPS unavailable — enter coordinates manually</div>
            )}
            {showManual && !gps && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: gpsError ? '0' : '8px' }}>
                <input value={manualLat} onChange={e => setManualLat(e.target.value)} placeholder="Latitude (e.g. 25.2048)"
                  style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px', color: C.textPrimary, fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', outline: 'none', width: '100%' }} />
                <input value={manualLng} onChange={e => setManualLng(e.target.value)} placeholder="Longitude (e.g. 55.2708)"
                  style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '8px 10px', color: C.textPrimary, fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', outline: 'none', width: '100%' }} />
              </div>
            )}
            {!showManual && !gpsLoading && !gps && (
              <button onClick={() => setShowManual(true)} style={{ fontSize: '12px', color: C.blue, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Enter manually</button>
            )}
          </div>

          {/* Action buttons */}
          {!action && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => handleAction('SAFE')} style={{ padding: '18px', borderRadius: '12px', border: 'none', background: C.green, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <CheckCircle size={22} /> I AM SAFE
              </button>
              <button onClick={() => setAction('NEED_HELP')} style={{ padding: '18px', borderRadius: '12px', border: 'none', background: C.red, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', animation: 'alertPulse 2s infinite' }}>
                <AlertTriangle size={22} /> NEED HELP
              </button>
            </div>
          )}

          {/* NEED_HELP: Emergency type */}
          {action === 'NEED_HELP' && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <AlertTriangle size={40} color={C.red} style={{ marginBottom: '10px' }} />
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: C.red, margin: '0 0 4px' }}>Emergency Alert</h3>
                <p style={{ fontSize: '13px', color: C.textSecondary }}>Select the type of emergency</p>
                {effectiveGps && <p style={{ fontSize: '11px', color: C.green, marginTop: '6px', fontFamily: 'JetBrains Mono, monospace' }}>GPS: {effectiveGps.lat.toFixed(5)}, {effectiveGps.lng.toFixed(5)}</p>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[['MEDICAL','Medical',C.pink,Stethoscope],['FIRE','Fire',C.orange,Flame],['SECURITY','Security',C.purple,Lock],['OTHER','Other',C.amber,HelpCircle]].map(([type,label,color,Icon]) => (
                  <button key={type} onClick={() => handleEmergencyType(type)}
                    style={{ padding: '18px 10px', borderRadius: '12px', border: `2px solid ${color}`, background: color+'22', color, fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <Icon size={24} />{label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
